import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/takeUntil';
import { OffboardingAction, OffboardingCase, OffboardingMutation } from '../../models/offboarding.model';
import { AssetAction, WorkflowError } from '../../models/employee-asset.model';
import { ClothingTypeReturnRequest } from '../../models/clothing-disposition.model';
import { OffboardingService } from '../../services/offboarding.service';
import { ASSET } from '../common/employee-assets/asset.constants';
import { assetErrorKey, assetOperationId, assetToday } from '../common/employee-assets/asset.helpers';
import { OFFBOARDING as O } from './offboarding.constants';

@Component({selector: 'app-offboarding-collection', templateUrl: './offboarding-collection.component.html',
  styleUrls: ['./offboarding-collection.component.css', './offboarding-workspace-content.css']})
export class OffboardingCollectionComponent implements OnChanges, OnDestroy {
  @Input() item: OffboardingCase;
  @Input() row: OffboardingAction;
  @Input() disabled = false;
  @Output() saved = new EventEmitter<OffboardingCase>();
  @Output() refresh = new EventEmitter<void>();
  @Output() working = new EventEmitter<boolean>();
  constants = O;
  assetConstants = ASSET;
  busy = false;
  formOpen = false;
  errorKey = ASSET.empty;
  draft: AssetAction;
  private assetPending: AssetAction;
  private clothingPending: ClothingTypeReturnRequest;
  private confirmation: OffboardingMutation;
  private context = ASSET.empty;
  private checking = false;
  private command: Subscription;
  private destroyed = new Subject<void>();

  constructor(private api: OffboardingService) {}

  get blocked(): boolean { return this.errorKey === ASSET.conflictKey || this.errorKey === ASSET.deniedKey; }
  get canReceive(): boolean {
    return !this.busy && !this.disabled && !this.blocked && this.row.status !== O.completed
      && (!!this.row.canReturn || !!this.row.canConfirm || !!this.confirmation || !!this.assetPending || !!this.clothingPending);
  }
  get retrying(): boolean { return !!this.confirmation || !!this.assetPending || !!this.clothingPending; }

  ngOnChanges(changes?: SimpleChanges): void {
    const context = JSON.stringify([this.item.id, this.row.key]);
    if (this.context !== context) { this.reset(); this.context = context; }
    if (this.checking && changes && (changes.item || changes.row)) { this.review(); }
    if (this.row.status === O.completed) { this.clearPending(); this.formOpen = false; }
  }

  receive(): void {
    if (!this.canReceive) { return; }
    if (this.confirmation || this.row.canConfirm) { this.confirm(this.item); return; }
    if (this.assetPending || this.clothingPending) { this.receipt(); return; }
    if (this.row.inputKind === O.assetReturn) { this.openAsset(); return; }
    this.clothingPending = {operationId: assetOperationId(), effectiveDate: assetToday(), units: this.row.clothingUnits || []};
    this.receipt();
  }

  submitAsset(): void {
    if (!this.canReceive || !this.draft || !this.draft.confirmed) { return; }
    if (!this.draft.quantity || this.draft.quantity < ASSET.one || this.draft.quantity > this.row.asset.remainingQuantity
      || this.draft.quantity % ASSET.one !== ASSET.zero) { return; }
    this.assetPending = Object.assign({}, this.draft, {reason: this.draft.reason.trim()});
    this.receipt();
  }

  checkCurrent(): void { if (!this.busy && !this.disabled) { this.checking = true; this.refresh.emit(); } }

  private openAsset(): void {
    if (!this.row.asset) { this.refresh.emit(); return; }
    if (!this.draft) {
      this.draft = {operationId: assetOperationId(), expectedVersion: this.row.asset.version, action: ASSET.returnAction,
        quantity: this.row.asset.remainingQuantity, reason: ASSET.empty, confirmed: false, damaged: false};
    }
    this.formOpen = true;
  }

  private receipt(): void {
    this.setBusy(true); this.errorKey = ASSET.empty;
    const request: Observable<OffboardingCase> = this.row.inputKind === O.assetReturn
      ? this.api.receiveAsset(this.item.id, this.row.key, this.assetPending)
      : this.api.receiveClothing(this.item.id, this.row.key, this.clothingPending);
    this.command = request.takeUntil(this.destroyed).subscribe(result => this.received(result), error => this.fail(error));
  }

  private received(result: OffboardingCase): void {
    this.assetPending = null; this.clothingPending = null; this.draft = null; this.formOpen = false;
    const row = (result.actions || []).filter(action => action.key === this.row.key)[ASSET.zero];
    if (row && row.canConfirm) { this.confirm(result); return; }
    this.setBusy(false); this.saved.emit(result);
  }

  private confirm(value: OffboardingCase): void {
    if (!this.confirmation) {
      this.confirmation = {operationId: assetOperationId(), expectedVersion: value.version, action: O.confirm, key: this.row.key};
    }
    this.setBusy(true); this.errorKey = ASSET.empty;
    this.command = this.api.act(value.id, this.confirmation).takeUntil(this.destroyed).subscribe(result => {
      this.clearPending(); this.setBusy(false); this.saved.emit(result);
    }, error => { this.saved.emit(value); this.fail(error); });
  }

  private review(): void {
    if (this.blocked || this.errorKey === ASSET.validationKey) { this.clearPending(); this.renewDraft(); }
    if (this.row.canConfirm) { this.assetPending = null; this.clothingPending = null; this.draft = null; this.formOpen = false; }
    this.checking = false; this.errorKey = ASSET.empty;
  }
  private renewDraft(): void {
    if (!this.draft || !this.row.asset || !this.row.canReturn) { this.draft = null; this.formOpen = false; return; }
    this.draft.operationId = assetOperationId(); this.draft.expectedVersion = this.row.asset.version;
    this.draft.quantity = this.row.asset.remainingQuantity; this.draft.confirmed = false; this.formOpen = true;
  }
  private fail(error: WorkflowError): void { this.setBusy(false); this.errorKey = assetErrorKey(error, true); }
  private setBusy(value: boolean): void { this.busy = value; this.working.emit(value); }
  private clearPending(): void { this.assetPending = null; this.clothingPending = null; this.confirmation = null; }
  private reset(): void {
    if (this.command) { this.command.unsubscribe(); }
    this.clearPending(); this.draft = null; this.formOpen = false; this.errorKey = ASSET.empty;
    this.checking = false; this.setBusy(false);
  }
  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/offboarding/offboarding-collection.component.ts